"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { ProfileData } from "@/features/candidate/lib/types";
import { transformProfile } from "@/features/search/lib/transform-profile";
import { useRunSearch } from "@/hooks/use-run-search";
import { useSavedSearch } from "@/hooks/use-search";
import {
  basePoolIdsFor,
  definitionDirty,
  definitionForPost,
  definitionKey,
  useSearchDefinitionDraft,
  type SearchCriterion,
  type SearchDefinition,
} from "@/hooks/use-search-definition-draft";
import {
  defaultSearchPoolIds,
  filterNotAvailableMessage,
} from "@/features/talent-pools/lib/filter-categories";
import { useListTalentPools } from "@/lib/api/generated";
import { useSearchResults } from "@/hooks/use-search-results";
import type {
  SavedSearchSchema,
  SearchFiltersSchema,
  SearchWithRunResponseSchema,
} from "@/lib/api/model";
import { ApiError, getApiErrorMessage } from "@/lib/api/error";
import { isQuotaError } from "@/lib/api/quota";
import { showToast } from "@/components/ui/sonner";
import {
  isRunning,
  selectRun,
  useSearchRunsStore,
  type RunState,
} from "@/stores/search-runs-store";
import {
  TALENT_SEARCH_PER_PAGE,
  TalentSearchDraftFiltersProvider,
} from "./talent-search-filters-provider";
import { useTalentSearch } from "./talent-search-provider";

export interface TalentSearchResultsContextValue {
  /**
   * The URL-bound search id this provider renders. Consumers that mutate "the
   * active search" (retry, export, feedback) should prefer this over
   * `useTalentSearch().currentSearchId`: it is always the search the user is
   * looking at, even before the saved-search GET has resolved.
   */
  searchId: string;
  /** The saved search once its own payload has landed (never another search's). */
  search: SavedSearchSchema | null;
  /** Latest run as tracked by the runs store (hydrated from GET + SSE). */
  run: RunState | undefined;

  // ── Unsaved definition draft (Save is Run) ──────────────────────────
  draft: SearchDefinition;
  setFilters: (filters: SearchFiltersSchema) => void;
  updateFilter: <K extends keyof SearchFiltersSchema>(
    key: K,
    value: SearchFiltersSchema[K],
  ) => void;
  setCriteria: (criteria: SearchCriterion[]) => void;
  setRerankQuery: (rerankQuery: string | null) => void;
  /** Select the talent pools the draft searches (sorted, 1..10). */
  setPoolIds: (poolIds: string[]) => void;
  resetDraft: () => void;
  /** Draft differs from the saved definition after canonicalisation. */
  dirty: boolean;
  /** Draft carries no search intent (no filters, criteria or query). */
  isEmpty: boolean;

  // ── Runs ────────────────────────────────────────────────────────────
  /** Run the current draft. */
  runNow: () => Promise<void>;
  /** Cancel the in-flight run. */
  cancelRun: () => Promise<void>;
  /** Re-run the saved definition (failure / cancelled recovery). */
  retry: () => Promise<void>;
  isRunPending: boolean;

  // ── Results (one page of the latest READY run) ──────────────────────
  profiles: ProfileData[];
  totalResults: number;
  page: number;
  setPage: (page: number) => void;
  perPage: number;
  /**
   * The current page is not yet on screen: first fetch for a ready run, or a
   * page change still resolving (stale rows stay visible meanwhile).
   */
  resultsLoading: boolean;
  /** Results page (or saved-search GET) failed; the page-cap 429 is excluded. */
  resultsError: Error | null;
  /** First saved-search GET for this id still in flight. */
  isHydrating: boolean;
}

const TalentSearchResultsContext =
  createContext<TalentSearchResultsContextValue | null>(null);

const EMPTY_PROFILES: ProfileData[] = [];

/**
 * Saved-search poll cadence while a run is in flight without a live stream
 * covering the search's project.
 */
export const DISCONNECTED_POLL_MS = 3000;

function toError(value: unknown): Error | null {
  if (!value) return null;
  return value instanceof Error ? value : new Error(String(value));
}

interface TalentSearchResultsProviderProps {
  searchId: string;
  children: ReactNode;
}

export function TalentSearchResultsProvider({
  searchId,
  children,
}: TalentSearchResultsProviderProps) {
  const { currentProjectId, setCurrentProjectId } = useTalentSearch();
  const markSeen = useSearchRunsStore((s) => s.markSeen);

  // ── Saved search + run ───────────────────────────────────────────────
  const run = useSearchRunsStore(selectRun(searchId));
  const running = isRunning(run?.phase);
  // Phase updates normally arrive over the project run stream. While a run
  // is in flight and no live stream covers this search's project (offline,
  // backoff exhausted, tab asleep, or the layout still streaming the project
  // the user came from), poll the saved search instead so the run still
  // resolves on screen; the store's ordering rules make the two feeds
  // converge. The search's project is only known from the payload itself,
  // hence the function form: until a payload for this id has landed, the
  // stream cannot be trusted to cover it.
  const streamConnected = useSearchRunsStore((s) => s.streamConnected);
  const streamProjectId = useSearchRunsStore((s) => s.streamProjectId);
  const pollInterval = useCallback(
    (data: SearchWithRunResponseSchema | undefined) => {
      if (!running) return false;
      const projectId =
        data?.search.id === searchId ? data.search.project_id : null;
      const covered =
        streamConnected && projectId !== null && streamProjectId === projectId;
      return covered ? false : DISCONNECTED_POLL_MS;
    },
    [running, searchId, streamConnected, streamProjectId],
  );
  // `useSavedSearch` keeps the previous search's payload as placeholder data
  // across navigation, so only trust a payload whose id matches the URL.
  const savedQuery = useSavedSearch(searchId, { refetchInterval: pollInterval });
  const search =
    savedQuery.data && savedQuery.data.search.id === searchId
      ? savedQuery.data.search
      : null;
  const isHydrating = !search && !savedQuery.isError;

  useEffect(() => {
    if (search?.project_id) setCurrentProjectId(search.project_id);
  }, [search?.project_id, setCurrentProjectId]);

  useEffect(() => {
    if (run?.phase === "ready") markSeen(searchId);
  }, [run?.phase, run?.latestReadyRunId, searchId, markSeen]);

  // ── Draft ────────────────────────────────────────────────────────────
  // `useRunSearch` writes the run response into the saved-search cache, so
  // after a Run the server definition moves to what this tab posted. The
  // draft hook is told what that was (`postedKey`, set before the request)
  // so it treats that arrival as confirmation and leaves the draft alone:
  // `dirty` clears when nothing was typed since the click and stays true for
  // an edit that landed meanwhile. When the backend instead hands the same
  // run back for an unchanged definition, nothing in the cache moves, so
  // `runNow` bumps `runSeq` to re-seed explicitly, and only when the draft
  // still equals what it posted. Nothing else re-seeds on its own: a Retry
  // of the saved definition, or a teammate's run landing through a refetch
  // with a new run id but the same definition, must never discard edits
  // typed in this tab. (A teammate's run with a different definition does
  // re-seed, as an agent apply does.)
  const [runSeq, setRunSeq] = useState(0);
  const [posted, setPosted] = useState<{
    searchId: string;
    key: string;
  } | null>(null);
  const postedKey = posted?.searchId === searchId ? posted.key : null;
  const poolsQuery = useListTalentPools();
  const serverDefinition = search?.definition ?? null;
  const basePoolIds = useMemo(
    () =>
      basePoolIdsFor(
        serverDefinition,
        poolsQuery.data ? defaultSearchPoolIds(poolsQuery.data.items) : null,
      ),
    [serverDefinition, poolsQuery.data],
  );
  const {
    draft,
    setFilters,
    updateFilter,
    setCriteria,
    setRerankQuery,
    setPoolIds,
    reset: resetDraft,
    dirty,
    isEmpty,
  } = useSearchDefinitionDraft(
    searchId,
    serverDefinition,
    String(runSeq),
    postedKey,
    basePoolIds,
  );
  // Latest draft for `runNow` to compare against after its request resolves.
  const draftRef = useRef(draft);
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  // Unsaved-changes guard: a dirty draft lives only in memory, so closing or
  // reloading the tab would silently drop it. Browsers ignore the message
  // text; cancelling the event is what surfaces their own confirm dialog.
  // (Next's App Router has no in-app route-change hook to attach to.)
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // ── Paging ───────────────────────────────────────────────────────────
  // Render-time reset: a new search or a newly ready run lands on page 1
  // without an effect round-trip (and without a stale-page flash).
  const readyRunId = run?.latestReadyRunId ?? null;
  const pageKey = `${searchId}:${readyRunId ?? ""}`;
  const [paging, setPaging] = useState({ key: pageKey, page: 1 });
  const page = paging.key === pageKey ? paging.page : 1;
  const setPage = useCallback(
    (next: number) => setPaging({ key: pageKey, page: Math.max(1, next) }),
    [pageKey],
  );

  // ── Results page ─────────────────────────────────────────────────────
  const resultsQuery = useSearchResults(searchId, page, TALENT_SEARCH_PER_PAGE);
  // `keepPreviousData` can hand us another run's (or search's) page while the
  // new key resolves; only rows stamped with the run we are showing count.
  const resultsData =
    resultsQuery.data && readyRunId && resultsQuery.data.run_id === readyRunId
      ? resultsQuery.data
      : undefined;

  // While a re-run is in flight the stage shows placeholders, and consumers
  // that stay mounted above it (the toolbar) must see no rows either, so the
  // earlier run's cached page is withheld until the run stops.
  const profiles = useMemo<ProfileData[]>(() => {
    const cards = running ? undefined : resultsData?.profiles;
    if (!cards?.length) return EMPTY_PROFILES;
    return cards.map((card) => transformProfile(card));
  }, [resultsData, running]);

  // A page blocked by the per-search page cap (free plan) is a soft limit,
  // not a failure: the central quota handler already toasts it. Snap back to
  // the last viewable page instead of surfacing an error panel.
  const pageLimitError =
    resultsQuery.error instanceof ApiError &&
    resultsQuery.error.status === 429 &&
    resultsQuery.error.detail?.quota_key === "pages_per_search"
      ? resultsQuery.error
      : null;

  const lastGoodPageRef = useRef(page);
  useEffect(() => {
    if (resultsData && !resultsQuery.isPlaceholderData) {
      lastGoodPageRef.current = resultsData.page;
    }
  }, [resultsData, resultsQuery.isPlaceholderData]);

  useEffect(() => {
    if (!pageLimitError) return;
    const cap =
      typeof pageLimitError.detail?.limit === "number"
        ? pageLimitError.detail.limit
        : lastGoodPageRef.current;
    if (page > cap) setPage(cap);
  }, [pageLimitError, page, setPage]);

  const resultsError = pageLimitError
    ? null
    : toError(resultsQuery.error ?? savedQuery.error);

  // Mirrors `useSearchResults`' gate: a page is expected whenever a READY run
  // exists and nothing newer is in flight (ready, or failed / cancelled
  // re-run still showing the earlier rows).
  const resultsExpected = !!run && !isRunning(run.phase) && !!readyRunId;
  const resultsLoading =
    resultsExpected &&
    !resultsError &&
    (!resultsData || resultsQuery.isPlaceholderData);

  const totalResults =
    resultsData?.total_count ?? run?.totalCount ?? search?.result_count ?? 0;

  // ── Run mutations ────────────────────────────────────────────────────
  const { runExisting, cancel, isPending: isRunPending } = useRunSearch();

  // `useRunSearch` opts its mutations out of the generic global error toast,
  // so this is the one place a failed run is surfaced. Quota 429s are the
  // exception: the query client's central handler already toasts those with
  // the upgrade CTA, so toasting here too would double up.
  const toastRunError = useCallback((err: unknown) => {
    if (isQuotaError(err)) return;
    const unavailable = filterNotAvailableMessage(err);
    if (unavailable) {
      showToast.error("Filters not available", { description: unavailable });
      return;
    }
    showToast.error("Run failed", { description: getApiErrorMessage(err) });
  }, []);

  const runNow = useCallback(async () => {
    const definition = definitionForPost(draft);
    // Tell the draft hook what is on its way so the response's definition is
    // read as confirmation rather than as a change to re-seed from.
    setPosted({ searchId, key: definitionKey(definition) });
    try {
      await runExisting({ searchId, definition });
      // Idempotent response (same run, same definition): the cache did not
      // move, so re-seed to clear `dirty`. Edits typed while the request was
      // in flight are the user's to run or discard, so leave those alone.
      if (!definitionDirty(draftRef.current, definition)) {
        setRunSeq((n) => n + 1);
      }
    } catch (err) {
      setPosted(null);
      toastRunError(err);
    }
  }, [draft, runExisting, searchId, toastRunError]);

  const savedDefinition = search?.definition ?? null;
  const retry = useCallback(async () => {
    if (!savedDefinition) return;
    try {
      await runExisting({ searchId, definition: savedDefinition });
    } catch (err) {
      toastRunError(err);
    }
  }, [runExisting, savedDefinition, searchId, toastRunError]);

  const projectId = search?.project_id ?? currentProjectId;
  const cancelRun = useCallback(async () => {
    if (!projectId) return;
    try {
      await cancel({ searchId, projectId });
    } catch (err) {
      showToast.error("Cancel failed", {
        description: getApiErrorMessage(err),
      });
    }
  }, [cancel, projectId, searchId]);

  const value = useMemo<TalentSearchResultsContextValue>(
    () => ({
      searchId,
      search,
      run,
      draft,
      setFilters,
      updateFilter,
      setCriteria,
      setRerankQuery,
      setPoolIds,
      resetDraft,
      dirty,
      isEmpty,
      runNow,
      cancelRun,
      retry,
      isRunPending,
      profiles,
      totalResults,
      page,
      setPage,
      perPage: TALENT_SEARCH_PER_PAGE,
      resultsLoading,
      resultsError,
      isHydrating,
    }),
    [
      searchId,
      search,
      run,
      draft,
      setFilters,
      updateFilter,
      setCriteria,
      setRerankQuery,
      setPoolIds,
      resetDraft,
      dirty,
      isEmpty,
      runNow,
      cancelRun,
      retry,
      isRunPending,
      profiles,
      totalResults,
      page,
      setPage,
      resultsLoading,
      resultsError,
      isHydrating,
    ],
  );

  return (
    <TalentSearchResultsContext.Provider value={value}>
      <TalentSearchDraftFiltersProvider
        filters={draft.filters}
        setFilters={setFilters}
        updateFilter={updateFilter}
        poolIds={draft.pool_ids ?? null}
        setPoolIds={setPoolIds}
        page={page}
        setPage={setPage}
        perPage={TALENT_SEARCH_PER_PAGE}
      >
        {children}
      </TalentSearchDraftFiltersProvider>
    </TalentSearchResultsContext.Provider>
  );
}

export function useTalentSearchResults() {
  const context = useContext(TalentSearchResultsContext);
  if (!context) {
    throw new Error(
      "useTalentSearchResults must be used within TalentSearchResultsProvider",
    );
  }
  return context;
}

export function useOptionalTalentSearchResults() {
  return useContext(TalentSearchResultsContext);
}
